<template>
  <div class="min-h-screen bg-gray-100">
    <div class="mx-auto p-6">
      <div class="flex justify-between items-start mb-6 mdmax:flex-col gap-4">
        <div>
          <button
            @click="$router.push('/invoices/resi')"
            class="text-sm text-neutral-600 hover:text-neutral-800 mb-2"
          >
            ← Kembali
          </button>
          <h1 class="text-xl font-bold text-neutral-800">Resi Detail</h1>
          <p class="text-sm text-neutral-500">
            {{ resi?.resi_number || "-" }} • {{ formatDate(resi?.date) }}
          </p>
        </div>

        <div class="flex gap-2">
          <button
            @click="openEditResiModal"
            class="px-4 py-2 rounded-lg bg-white text-neutral-700 border border-neutral-300 hover:bg-neutral-50 font-medium"
          >
            Edit Resi
          </button>
          <button
            @click="openDeleteResiModal"
            class="px-4 py-2 rounded-lg bg-red-500 text-white hover:bg-red-600 font-medium"
          >
            Delete Resi
          </button>
        </div>
      </div>

      <div class="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div class="bg-white rounded-lg p-4 shadow-sm">
          <p class="text-sm text-neutral-500">Customer</p>
          <p class="text-neutral-800 font-bold">
            {{
              resi?.customer_name ||
              resi?.invoice_customer?.customer_name ||
              "-"
            }}
          </p>
          <p class="text-neutral-500 text-sm">
            {{
              resi?.customer_address ||
              resi?.invoice_customer?.customer_address ||
              "-"
            }}
          </p>
        </div>
        <div class="bg-white rounded-lg p-4 shadow-sm">
          <p class="text-sm text-neutral-500">Pricing</p>
          <div class="flex justify-between items-center">
            <span class="text-sm text-neutral-500">Destination</span>
            <span class="font-bold text-neutral-800">{{ resi?.destination || "-" }}</span>
          </div>
          <div class="flex justify-between items-center">
            <span class="text-sm text-neutral-500">Unit Price</span>
            <span class="font-bold text-neutral-800">{{
              formatCurrency(resi?.unit_price)
            }}</span>
          </div>
          <div class="flex justify-between items-center mt-1">
            <span class="text-sm text-neutral-500">Minimum Weight</span>
            <span class="font-bold text-neutral-800">{{
              resi?.minimum_weight ? resi.minimum_weight + ' Kg' : '-'
            }}</span>
          </div>
          <div class="flex justify-between items-center mt-1">
            <span class="text-sm text-neutral-500">Total Price</span>
            <span class="font-bold text-neutral-800">{{
              formatCurrency(resi?.total_price)
            }}</span>
          </div>
          <div class="mt-2">
            <span
              class="px-2 py-1 rounded text-xs font-medium"
              :class="
                resi?.eligible
                  ? 'bg-green-100 text-green-700'
                  : 'bg-gray-100 text-gray-700'
              "
            >
              {{ resi?.eligible ? "Eligible" : "Not eligible" }}
            </span>
          </div>
        </div>
        <div class="bg-white rounded-lg p-4 shadow-sm">
          <p class="text-sm text-neutral-500 mb-2">Flags</p>
          <div class="grid grid-cols-1 gap-1 text-sm text-neutral-700">
            <span>has_weight_detail: {{ resi?.has_weight_detail ? "✓" : "—" }}</span>
            <span
              >has_white_receipt:
              {{ resi?.has_white_receipt ? "✓" : "—" }}</span
            >
            <span
              >has_blue_receipt:
              {{ resi?.has_blue_receipt ? "✓" : "—" }}</span
            >
            <span
              >has_delivery_order:
              {{ resi?.has_delivery_order ? "✓" : "—" }}</span
            >
            <span
              >is_wooden_packing:
              {{ resi?.is_wooden_packing ? "✓" : "—" }}</span
            >
          </div>
        </div>
      </div>

      <div class="flex justify-between items-end mb-4 mdmax:flex-col gap-3">
        <div>
          <h2 class="text-lg font-bold text-neutral-800">Packages</h2>
          <p class="text-sm text-neutral-500">
            Tambah package dengan dimensi atau manual weight.
          </p>
        </div>
        <button
          @click="openPackageModal('create')"
          class="px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 font-medium"
        >
          + Tambah Package
        </button>
      </div>

      <TableComponent :columns="packageColumns" :list="packageTableData">
        <template #table-row="{ entry, column }">
          <span v-if="column.field === 'action'" class="flex gap-2">
            <button
              @click="openPackageModal('edit', entry)"
              class="px-3 py-1 rounded bg-neutral-200 text-neutral-700 hover:bg-neutral-300 text-sm"
            >
              Edit
            </button>
            <button
              @click="openDeletePackageModal(entry)"
              class="px-3 py-1 rounded bg-red-500 text-white hover:bg-red-600 text-sm"
            >
              Delete
            </button>
          </span>
          <span v-else-if="column.field === 'RowNumber'" class="font-medium">
            {{ entry.RowNumber }}
          </span>
          <span v-else-if="column.field === 'shipment_date'">
            {{ formatDate(entry.shipment_date) }}
          </span>
          <span v-else-if="column.field === 'dimension'">
            <span v-if="entry.length && entry.width && entry.height">
              {{ entry.length }} x {{ entry.width }} x {{ entry.height }}
            </span>
            <span v-else>-</span>
          </span>
          <span v-else-if="column.field === 'weight'">
            {{ entry.weight || "-" }}
          </span>
          <span v-else-if="column.field === 'is_wooden_packing'">
            <span
              v-if="entry.is_wooden_packing"
              class="px-2 py-1 rounded text-xs font-medium bg-amber-100 text-amber-700"
            >
              Packing Kayu
            </span>
            <span v-else>-</span>
          </span>
          <span v-else-if="column.field === 'wooden_packaging_price'">
            <span v-if="entry.wooden_packaging_price">
              {{ formatCurrency(entry.wooden_packaging_price) }}
            </span>
            <span v-else>Rp 0</span>
          </span>
          <span v-else-if="column.field === 'price'">
            {{ formatCurrency(getPackagePrice(entry)) }}
          </span>
          <span v-else>{{ entry[column.field] || "-" }}</span>
        </template>
      </TableComponent>

      <div
        v-if="packageTableData.length > 0"
        class="mt-6 mb-6 flex justify-between items-center mdmax:flex-col mdmax:items-start gap-4"
      >
        <div v-if="!isMobile" class="text-sm font-medium text-neutral-500">
          Menampilkan {{ packageStart }} - {{ packageEnd }} dari
          {{ totalPackages }}
        </div>
        <Pagination
          :currentPage="packagePage"
          :totalCount="totalPackages"
          :pageSize="packagePerPage"
          @onPageChange="changePackagePage"
        />
      </div>

      <Modal
        :modalVisible="editResiModalVisible"
        @close="closeEditResiModal"
        width="720px"
      >
        <div slot="modal-content" class="text-left">
          <h3 class="text-lg font-bold text-neutral-800 mb-4">Edit Resi</h3>

          <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div class="md:col-span-2">
              <TextField
                type="date"
                label="Date"
                required
                v-model="editResiForm.date"
              />
            </div>
            <TextField
              label="Destination"
              required
              v-model="editResiForm.destination"
              placeholder="Destination"
            />
            <TextField
              label="Resi Number"
              required
              v-model="editResiForm.resi_number"
              placeholder="Resi number"
            />
            <div class="md:col-span-2">
              <div class="grid grid-cols-1 md:grid-cols-2 gap-3">
                <label class="flex items-center gap-2 text-sm text-neutral-700">
                  <input
                    type="checkbox"
                    class="w-4 h-4"
                    v-model="editResiForm.has_weight_detail"
                  />
                  has_weight_detail
                </label>
                <label class="flex items-center gap-2 text-sm text-neutral-700">
                  <input
                    type="checkbox"
                    class="w-4 h-4"
                    v-model="editResiForm.has_white_receipt"
                  />
                  has_white_receipt
                </label>
                <label class="flex items-center gap-2 text-sm text-neutral-700">
                  <input
                    type="checkbox"
                    class="w-4 h-4"
                    v-model="editResiForm.has_blue_receipt"
                  />
                  has_blue_receipt
                </label>
                <label class="flex items-center gap-2 text-sm text-neutral-700">
                  <input
                    type="checkbox"
                    class="w-4 h-4"
                    v-model="editResiForm.has_delivery_order"
                  />
                  has_delivery_order
                </label>
                <label class="flex items-center gap-2 text-sm text-neutral-700">
                  <input
                    type="checkbox"
                    class="w-4 h-4"
                    v-model="editResiForm.is_wooden_packing"
                  />
                  is_wooden_packing
                </label>
              </div>
            </div>
          </div>

          <div class="flex justify-end gap-3 mt-6">
            <button
              @click="closeEditResiModal"
              class="px-4 py-2 rounded-lg border border-neutral-300 text-neutral-600 hover:bg-neutral-50 font-medium"
            >
              Batal
            </button>
            <button
              @click="submitEditResi"
              :disabled="!editResiForm.destination || !editResiForm.resi_number"
              class="px-4 py-2 rounded-lg bg-primary-500 text-white hover:bg-primary-600 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Simpan
            </button>
          </div>
        </div>
      </Modal>

      <Modal
        :modalVisible="deleteResiModalVisible"
        @close="closeDeleteResiModal"
        width="400px"
      >
        <div slot="modal-content" class="text-center">
          <h3 class="text-lg font-bold text-neutral-800 mb-2">Delete Resi</h3>
          <p class="text-neutral-600 mb-6">
            Apakah Anda yakin ingin menghapus resi
            <strong>{{ resi?.resi_number }}</strong
            >?
          </p>
          <div class="flex justify-center gap-3">
            <button
              @click="closeDeleteResiModal"
              class="px-4 py-2 rounded-lg border border-neutral-300 text-neutral-600 hover:bg-neutral-50 font-medium"
            >
              Batal
            </button>
            <button
              @click="confirmDeleteResi"
              class="px-4 py-2 rounded-lg bg-red-500 text-white hover:bg-red-600 font-medium"
            >
              Hapus
            </button>
          </div>
        </div>
      </Modal>

      <Modal
        :modalVisible="packageModalVisible"
        @close="closePackageModal"
        width="720px"
      >
        <div slot="modal-content" class="text-left">
          <h3 class="text-lg font-bold text-neutral-800 mb-4">
            {{ packageModalMode === "create" ? "Tambah" : "Edit" }} Package
          </h3>

          <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
            <TextField
              label="Shipment Date"
              type="date"
              required
              v-model="packageForm.shipment_date"
            />
            <TextField
              label="Koli"
              type="number"
              v-model="packageForm.koli"
              placeholder="1"
            />

            <div class="md:col-span-2">
              <label class="flex items-center gap-2 text-sm text-neutral-700">
                <input
                  type="checkbox"
                  class="w-4 h-4"
                  v-model="packageForm.is_wooden_packing"
                  @change="onPackageWoodenPackingChange"
                />
                Packing Kayu
              </label>
              <p class="text-xs text-neutral-500 mt-1">
                Jika aktif, wajib input dimensions.
              </p>
            </div>

            <div
              class="md:col-span-2"
              v-if="!packageForm.is_wooden_packing"
            >
              <div class="flex gap-4">
                <label class="flex items-center gap-2 text-sm text-neutral-700">
                  <input
                    type="radio"
                    name="input-mode"
                    value="dims"
                    v-model="packageForm.input_mode"
                  />
                  Dimensions
                </label>
                <label class="flex items-center gap-2 text-sm text-neutral-700">
                  <input
                    type="radio"
                    name="input-mode"
                    value="manual"
                    v-model="packageForm.input_mode"
                  />
                  Manual Weight
                </label>
              </div>
            </div>

            <template v-if="shouldUseDims">
              <TextField
                label="Length"
                type="number"
                required
                v-model="packageForm.length"
                placeholder="0"
              />
              <TextField
                label="Width"
                type="number"
                required
                v-model="packageForm.width"
                placeholder="0"
              />
              <TextField
                label="Height"
                type="number"
                required
                v-model="packageForm.height"
                placeholder="0"
              />
            </template>

            <template v-else>
              <div class="md:col-span-2">
                <TextField
                  label="Weight"
                  type="number"
                  required
                  v-model="packageForm.weight"
                  placeholder="0"
                />
                <p class="text-xs text-neutral-500 mt-1">
                  Jika Packing Kayu aktif, wajib input dimensions.
                </p>
              </div>
            </template>
          </div>

          <div class="flex justify-end gap-3 mt-6">
            <button
              @click="closePackageModal"
              class="px-4 py-2 rounded-lg border border-neutral-300 text-neutral-600 hover:bg-neutral-50 font-medium"
            >
              Batal
            </button>
            <button
              @click="submitPackage"
              :disabled="!canSubmitPackage"
              class="px-4 py-2 rounded-lg bg-primary-500 text-white hover:bg-primary-600 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Simpan
            </button>
          </div>
        </div>
      </Modal>

      <Modal
        :modalVisible="deletePackageModalVisible"
        @close="closeDeletePackageModal"
        width="400px"
      >
        <div slot="modal-content" class="text-center">
          <h3 class="text-lg font-bold text-neutral-800 mb-2">
            Delete Package
          </h3>
          <p class="text-neutral-600 mb-6">
            Apakah Anda yakin ingin menghapus package ini?
          </p>
          <div class="flex justify-center gap-3">
            <button
              @click="closeDeletePackageModal"
              class="px-4 py-2 rounded-lg border border-neutral-300 text-neutral-600 hover:bg-neutral-50 font-medium"
            >
              Batal
            </button>
            <button
              @click="confirmDeletePackage"
              class="px-4 py-2 rounded-lg bg-red-500 text-white hover:bg-red-600 font-medium"
            >
              Hapus
            </button>
          </div>
        </div>
      </Modal>
    </div>
  </div>
</template>

<script>
import api from "@/api"
import dayjs from "dayjs"
import { mapActions, mapGetters } from "vuex"
import TableComponent from "@/components/Table/Table"
import Pagination from "@/components/Pagination/Pagination"
import TextField from "@/components/Forms/TextField"
import Modal from "@/components/Modal/Modal"

export default {
  name: "InvoicesResiDetail",
  components: {
    TableComponent,
    Pagination,
    TextField,
    Modal
  },
  data() {
    return {
      resi: null,

      editResiModalVisible: false,
      editResiForm: {
        date: "",
        destination: "",
        resi_number: "",
        has_weight_detail: false,
        has_white_receipt: false,
        has_blue_receipt: false,
        has_delivery_order: false,
        is_wooden_packing: false
      },
      deleteResiModalVisible: false,

      packageColumns: [
        { label: "No", field: "RowNumber" },
        { label: "Shipment Date", field: "shipment_date" },
        { label: "Koli", field: "koli" },
        { label: "Dimension", field: "dimension" },
        { label: "Weight", field: "weight" },
        { label: "Packing Kayu", field: "is_wooden_packing" },
        { label: "Wood Packaging Price", field: "wooden_packaging_price" },
        { label: "Price", field: "price" },
        { label: "Action", field: "action" }
      ],
      packages: [],
      packagesMeta: {},
      packagePage: 1,
      packagePerPage: 10,

      packageModalVisible: false,
      packageModalMode: "create",
      packageToEdit: null,
      packageForm: {
        shipment_date: dayjs().format("YYYY-MM-DD"),
        is_wooden_packing: false,
        koli: 1,
        input_mode: "dims",
        length: "",
        width: "",
        height: "",
        weight: ""
      },
      deletePackageModalVisible: false,
      packageToDelete: null
    }
  },
  computed: {
    ...mapGetters("common", ["isMobile"]),
    resiId() {
      return this.$route.params.id
    },
    totalPackages() {
      return (
        this.packagesMeta?.total_item ||
        this.packagesMeta?.total ||
        this.packagesMeta?.totalItems ||
        0
      )
    },
    packageTableData() {
      return (this.packages || []).map((item, index) => ({
        ...item,
        RowNumber: (this.packagePage - 1) * this.packagePerPage + index + 1
      }))
    },
    packageStart() {
      return this.totalPackages
        ? (this.packagePage - 1) * this.packagePerPage + 1
        : 0
    },
    packageEnd() {
      return Math.min(this.packagePage * this.packagePerPage, this.totalPackages)
    },
    shouldUseDims() {
      if (this.packageForm.is_wooden_packing) return true
      return this.packageForm.input_mode === "dims"
    },
    canSubmitPackage() {
      if (!this.packageForm.shipment_date) return false
      const koli = Number(this.packageForm.koli || 1)
      if (!koli || koli <= 0) return false

      if (this.shouldUseDims) {
        const l = Number(this.toDecimalString(this.packageForm.length, "0"))
        const w = Number(this.toDecimalString(this.packageForm.width, "0"))
        const h = Number(this.toDecimalString(this.packageForm.height, "0"))
        return l > 0 && w > 0 && h > 0
      }

      const weight = Number(this.toDecimalString(this.packageForm.weight, "0"))
      return weight > 0
    }
  },
  methods: {
    ...mapActions("common", ["showLoading", "hideLoading", "setToast"]),
    toastSuccess(message, secondaryText = "") {
      this.setToast({
        options: {
          type: "success",
          duration: 2500,
          primaryText: message,
          secondaryText
        }
      })
    },
    toastError(message, secondaryText = "") {
      this.setToast({
        options: {
          type: "danger",
          duration: 3500,
          primaryText: message,
          secondaryText
        }
      })
    },
    getErrorMessage(err, fallback = "Terjadi kesalahan") {
      return (
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        err?.message ||
        fallback
      )
    },
    unwrapList(envelope) {
      const data = envelope?.data?.data
      const meta = envelope?.data?.meta || envelope?.data?.data?.meta || {}
      const items = Array.isArray(data) ? data : data?.items || data?.data || []
      return { items, meta }
    },
    toDecimalString(value, fallback = "0") {
      const raw = String(value ?? "").trim()
      if (!raw) return fallback
      const normalized = raw.replace(/,/g, ".").replace(/[^0-9.-]/g, "")
      return normalized || fallback
    },
    formatCurrency(value) {
      const numberValue = Number(this.toDecimalString(value, "0"))
      if (Number.isNaN(numberValue)) return "Rp 0"
      return new Intl.NumberFormat("id-ID", {
        style: "currency",
        currency: "IDR",
        maximumFractionDigits: 0
      }).format(numberValue)
    },
    formatDate(dateString) {
      if (!dateString) return "-"
      const date = new Date(dateString)
      if (isNaN(date.getTime())) return dateString
      const months = [
        "Januari",
        "Februari",
        "Maret",
        "April",
        "Mei",
        "Juni",
        "Juli",
        "Agustus",
        "September",
        "Oktober",
        "November",
        "Desember"
      ]
      const day = String(date.getDate()).padStart(2, "0")
      const month = months[date.getMonth()]
      const year = date.getFullYear()
      return `${day} ${month} ${year}`
    },
    getPackagePrice(entry) {
      const candidates = [
        entry?.price,
        entry?.total_price,
        entry?.total_amount,
        entry?.amount,
        entry?.package_price,
        entry?.package_total_price,
        entry?.calculated_price,
        entry?.calculated_total_price,
        entry?.meta?.price,
        entry?.meta?.total_price,
        entry?.meta?.total_amount
      ]
      const found = candidates.find(
        (v) => v !== undefined && v !== null && String(v).trim() !== ""
      )
      if (found !== undefined) {
        return Number(this.toDecimalString(found, "0")) || 0
      }

      const unitPrice =
        Number(this.toDecimalString(this.resi?.unit_price, "0")) || 0
      if (!unitPrice) return 0

      const koli = Number(entry?.koli || 1) || 1
      const woodenPacking = !!entry?.is_wooden_packing

      const manualWeight =
        Number(this.toDecimalString(entry?.weight, "0")) || 0
      const length = Number(this.toDecimalString(entry?.length, "0")) || 0
      const width = Number(this.toDecimalString(entry?.width, "0")) || 0
      const height = Number(this.toDecimalString(entry?.height, "0")) || 0
      const hasDims = length > 0 && width > 0 && height > 0

      const divisor = woodenPacking ? 4000 : 6000
      const volumeWeight = hasDims
        ? (length * width * height) / divisor
        : 0

      let baseWeight = 0
      if (woodenPacking) {
        baseWeight = volumeWeight
      } else if (hasDims && manualWeight > 0) {
        baseWeight = Math.max(manualWeight, volumeWeight)
      } else if (hasDims) {
        baseWeight = volumeWeight
      } else {
        baseWeight = manualWeight
      }

      const totalWeight = baseWeight * koli
      if (!totalWeight) return 0

      return Math.round(totalWeight * unitPrice)
    },
    onPackageWoodenPackingChange() {
      if (this.packageForm.is_wooden_packing) {
        this.packageForm.input_mode = "dims"
      }
    },

    async fetchResiDetail() {
      this.showLoading()
      try {
        const res = await api.invoiceGenerator.resiDetail(this.resiId)
        this.resi = res?.data?.data || null
      } catch (err) {
        this.toastError(this.getErrorMessage(err, "Gagal memuat detail resi"))
      } finally {
        this.hideLoading()
      }
    },
    async fetchPackages() {
      this.showLoading()
      try {
        const res = await api.invoiceGenerator.resiPackages(this.resiId, {
          page: this.packagePage,
          per_page: this.packagePerPage
        })
        const { items, meta } = this.unwrapList(res)
        this.packages = items
        this.packagesMeta = meta || {}
      } catch (err) {
        this.toastError(this.getErrorMessage(err, "Gagal memuat packages"))
      } finally {
        this.hideLoading()
      }
    },
    changePackagePage(page) {
      this.packagePage = page
      this.fetchPackages()
    },

    openEditResiModal() {
      this.editResiForm = {
        date: this.resi?.date || "",
        destination: this.resi?.destination || "",
        resi_number: this.resi?.resi_number || "",
        has_weight_detail: !!this.resi?.has_weight_detail,
        has_white_receipt: !!this.resi?.has_white_receipt,
        has_blue_receipt: !!this.resi?.has_blue_receipt,
        has_delivery_order: !!this.resi?.has_delivery_order,
        is_wooden_packing: !!this.resi?.is_wooden_packing
      }
      this.editResiModalVisible = true
    },
    closeEditResiModal() {
      this.editResiModalVisible = false
    },
    async submitEditResi() {
      const payloads = {
        date: this.editResiForm.date,
        destination: this.editResiForm.destination,
        resi_number: this.editResiForm.resi_number,
        has_weight_detail: !!this.editResiForm.has_weight_detail,
        has_white_receipt: !!this.editResiForm.has_white_receipt,
        has_blue_receipt: !!this.editResiForm.has_blue_receipt,
        has_delivery_order: !!this.editResiForm.has_delivery_order,
        is_wooden_packing: !!this.editResiForm.is_wooden_packing
      }

      this.showLoading()
      try {
        const res = await api.invoiceGenerator.updateResi(this.resiId, payloads)
        this.toastSuccess(res?.data?.message || "Resi berhasil diupdate")
        this.closeEditResiModal()
        await this.fetchResiDetail()
        await this.fetchPackages()
      } catch (err) {
        this.toastError(this.getErrorMessage(err, "Gagal update resi"))
      } finally {
        this.hideLoading()
      }
    },

    openDeleteResiModal() {
      this.deleteResiModalVisible = true
    },
    closeDeleteResiModal() {
      this.deleteResiModalVisible = false
    },
    async confirmDeleteResi() {
      this.showLoading()
      try {
        const res = await api.invoiceGenerator.deleteResi(this.resiId)
        this.toastSuccess(res?.data?.message || "Resi berhasil dihapus")
        this.closeDeleteResiModal()
        this.$router.push("/invoices/resi")
      } catch (err) {
        this.toastError(this.getErrorMessage(err, "Gagal menghapus resi"))
      } finally {
        this.hideLoading()
      }
    },

    openPackageModal(mode, entry) {
      this.packageModalMode = mode
      this.packageToEdit = mode === "edit" ? entry : null
      const defaultWoodenPacking =
        mode === "create"
          ? !!this.resi?.is_wooden_packing
          : !!entry?.is_wooden_packing
      this.packageForm = {
        shipment_date:
          entry?.shipment_date || dayjs().format("YYYY-MM-DD"),
        is_wooden_packing: defaultWoodenPacking,
        koli: entry?.koli ?? 1,
        input_mode: "dims",
        length:
          entry?.length === 0 || entry?.length ? String(entry.length) : "",
        width: entry?.width === 0 || entry?.width ? String(entry.width) : "",
        height:
          entry?.height === 0 || entry?.height ? String(entry.height) : "",
        weight:
          entry?.weight === 0 || entry?.weight ? String(entry.weight) : ""
      }
      if (this.packageForm.is_wooden_packing) {
        this.packageForm.input_mode = "dims"
      } else if (this.packageForm.weight && !this.packageForm.length) {
        this.packageForm.input_mode = "manual"
      }
      this.packageModalVisible = true
    },
    closePackageModal() {
      this.packageModalVisible = false
      this.packageToEdit = null
    },
    async submitPackage() {
      const payloads = {
        shipment_date: this.packageForm.shipment_date,
        is_wooden_packing: !!this.packageForm.is_wooden_packing,
        koli: Number(this.packageForm.koli || 1)
      }

      if (this.shouldUseDims) {
        payloads.length = this.toDecimalString(this.packageForm.length, "0")
        payloads.width = this.toDecimalString(this.packageForm.width, "0")
        payloads.height = this.toDecimalString(this.packageForm.height, "0")
      } else {
        payloads.weight = this.toDecimalString(this.packageForm.weight, "0")
      }

      this.showLoading()
      try {
        let res
        if (this.packageModalMode === "create") {
          res = await api.invoiceGenerator.createPackage(this.resiId, payloads)
        } else {
          const packageId =
            this.packageToEdit?.id || this.packageToEdit?.package_id
          res = await api.invoiceGenerator.updatePackage(packageId, payloads)
        }
        this.toastSuccess(res?.data?.message || "Package berhasil disimpan")
        this.closePackageModal()
        this.fetchPackages()
        this.fetchResiDetail()
      } catch (err) {
        this.toastError(this.getErrorMessage(err, "Gagal menyimpan package"))
      } finally {
        this.hideLoading()
      }
    },

    openDeletePackageModal(entry) {
      this.packageToDelete = entry
      this.deletePackageModalVisible = true
    },
    closeDeletePackageModal() {
      this.deletePackageModalVisible = false
      this.packageToDelete = null
    },
    async confirmDeletePackage() {
      if (!this.packageToDelete) return
      const packageId =
        this.packageToDelete.id || this.packageToDelete.package_id

      this.showLoading()
      try {
        const res = await api.invoiceGenerator.deletePackage(packageId)
        this.toastSuccess(res?.data?.message || "Package berhasil dihapus")
        this.closeDeletePackageModal()
        this.fetchPackages()
        this.fetchResiDetail()
      } catch (err) {
        this.toastError(this.getErrorMessage(err, "Gagal menghapus package"))
      } finally {
        this.hideLoading()
      }
    }
  },
  async mounted() {
    await this.fetchResiDetail()
    await this.fetchPackages()
  }
}
</script>
